// Effect parameter editor. Renders sliders from a per-kind spec and writes
// changes live into the store (engine picks them up on the mixer change event).
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el, uid } from "../util";
import { openModal, toast } from "./modal";
import { EffectKind, EffectParam, FxPreset } from "../state/model";
import {
  getInsertProfile, setInsertProfile, buildNoiseProfile, captureNoiseFromMic, hasAudioSignal,
} from "../audio/noise-reduction";
import { clipQuietestMono } from "../audio/clipfx";
import { FX_PRESETS } from "../audio/fx-presets";
import { EFFECT_DEFAULTS } from "../audio/effects";

interface Spec { key: string; label: string; min: number; max: number; step: number; unit?: string; }

const SPECS: Record<Exclude<EffectKind, "wam">, Spec[]> = {
  eq: [
    { key: "low", label: "Low", min: -18, max: 18, step: 0.5, unit: "dB" },
    { key: "mid", label: "Mid", min: -18, max: 18, step: 0.5, unit: "dB" },
    { key: "midFreq", label: "Mid Freq", min: 200, max: 8000, step: 10, unit: "Hz" },
    { key: "high", label: "High", min: -18, max: 18, step: 0.5, unit: "dB" },
  ],
  compressor: [
    { key: "threshold", label: "Threshold", min: -60, max: 0, step: 1, unit: "dB" },
    { key: "ratio", label: "Ratio", min: 1, max: 20, step: 0.5, unit: ":1" },
    { key: "attack", label: "Attack", min: 0, max: 0.5, step: 0.001, unit: "s" },
    { key: "release", label: "Release", min: 0.01, max: 1.5, step: 0.01, unit: "s" },
    { key: "knee", label: "Knee", min: 0, max: 40, step: 1, unit: "dB" },
  ],
  delay: [
    { key: "time", label: "Time", min: 0, max: 2, step: 0.01, unit: "s" },
    { key: "feedback", label: "Feedback", min: 0, max: 0.95, step: 0.01 },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
  ],
  reverb: [
    { key: "decay", label: "Decay", min: 0.2, max: 6, step: 0.1, unit: "s" },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
  ],
  gain: [
    { key: "gain", label: "Gain", min: -24, max: 24, step: 0.5, unit: "dB" },
  ],
  lowpass: [
    { key: "cutoff", label: "Cutoff", min: 200, max: 18000, step: 10, unit: "Hz" },
    { key: "q", label: "Resonance", min: 0.1, max: 12, step: 0.1 },
  ],
  highpass: [
    { key: "cutoff", label: "Cutoff", min: 20, max: 8000, step: 10, unit: "Hz" },
    { key: "q", label: "Resonance", min: 0.1, max: 12, step: 0.1 },
  ],
  saturator: [
    { key: "drive", label: "Drive", min: 0, max: 1, step: 0.01 },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
    { key: "output", label: "Output", min: -24, max: 12, step: 0.5, unit: "dB" },
  ],
  chorus: [
    { key: "rate", label: "Rate", min: 0.1, max: 8, step: 0.1, unit: "Hz" },
    { key: "depth", label: "Depth", min: 1, max: 15, step: 0.5, unit: "ms" },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
  ],
  limiter: [
    { key: "threshold", label: "Ceiling", min: -30, max: 0, step: 0.5, unit: "dB" },
    { key: "release", label: "Release", min: 0.01, max: 0.5, step: 0.01, unit: "s" },
    { key: "output", label: "Makeup", min: -6, max: 18, step: 0.5, unit: "dB" },
  ],
  width: [
    { key: "width", label: "Width", min: 0, max: 2, step: 0.01 },
  ],
  denoise: [
    { key: "strength", label: "Strength", min: 0, max: 1, step: 0.05 },
    { key: "smoothing", label: "Smoothing", min: 0, max: 1, step: 0.05 },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
  ],
  deverb: [
    { key: "decay", label: "Decay (RT60)", min: 0.1, max: 4, step: 0.05, unit: "s" },
    { key: "damping", label: "Damping", min: 0, max: 1, step: 0.05 },
    { key: "reverb", label: "Reverb Level", min: -20, max: 15, step: 0.5, unit: "dB" },
    { key: "strength", label: "Strength", min: 0, max: 1, step: 0.05 },
    { key: "mix", label: "Mix", min: 0, max: 1, step: 0.01 },
  ],
};

export function openFxEditor(store: Store, engine: AudioEngine, target: string | "master", fxId: string, onWamGui?: (fxId: string) => void) {
  const list = target === "master" ? store.project.masterFx : store.track(target)?.fx;
  const fx = list?.find((f) => f.id === fxId);
  if (!fx) return;

  const body = el("div", { class: "fx-editor" });

  const enableRow = el("label", { class: "fx-param" });
  const en = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
  en.checked = fx.enabled;
  en.addEventListener("change", () => store.updateEffect(target, fxId, { enabled: en.checked }));
  enableRow.append(el("span", { text: "Enabled" }), en, el("span"));
  body.append(enableRow);

  if (fx.kind === "wam") {
    body.append(el("p", { class: "hint", text: "Web Audio Module plugin. Open its native UI to edit parameters." }));
    const guiBtn = el("button", { class: "active", text: "Open Plugin GUI" });
    guiBtn.addEventListener("click", () => onWamGui?.(fxId));
    body.append(guiBtn);
  } else {
    if (fx.kind === "denoise") {
      // Per-insert noise profile: each Denoise insert learns its OWN profile,
      // independent of other tracks. Sample from the mic or a selected clip's
      // quiet part; a presence gate rejects silence.
      let busy = false;
      const nrBtn = (label: string, on: () => void) => { const b = el("button", { text: label }); b.addEventListener("click", on); return b; };
      const panel = el("div", { class: "nr-panel" });
      const renderPanel = () => {
        panel.innerHTML = "";
        const prof = getInsertProfile(fxId);
        const actions = [nrBtn("🎙 From mic", sampleMic), nrBtn("📋 From selected clip", sampleClip)];
        if (prof) actions.push(nrBtn("Clear", clearProf));
        panel.append(
          el("div", { class: "nr-head" }, [
            el("span", { class: "nr-title", text: "🔇 Noise Profile (this insert)" }),
            el("span", { class: prof ? "badge live" : "badge", text: prof ? `Profile: ${prof.source}` : "No profile yet" }),
          ]),
          el("p", { class: "hint", text: "Live noise reduction using this insert's own profile — independent of other tracks. Adds ~21 ms latency." }),
          el("div", { class: "clip-actions" }, actions),
        );
      };
      async function sampleMic() {
        if (busy) return; busy = true;
        toast("Capturing ~1.5 s of ambient noise — stay quiet…", 2500);
        try {
          const mono = await captureNoiseFromMic(engine.ctx, { seconds: 1.5 });
          if (!hasAudioSignal(mono)) { toast("No audio detected from the mic — check your input."); return; }
          setInsertProfile(fxId, buildNoiseProfile(mono, engine.ctx.sampleRate, "live mic"));
          store.emitChange("mixer"); // push to this insert's live worklet
          toast("Noise profile captured for this insert.");
        } catch (e: any) { console.error(e); toast("Mic capture failed: " + (e?.message || e), 6000); }
        finally { busy = false; renderPanel(); }
      }
      function sampleClip() {
        const sel = store.selectedClipList();
        const clip = sel[0]?.clip;
        if (!clip) { toast("Select a clip first — its quietest part is sampled as noise."); return; }
        const buf = store.buffers.get(clip.assetId);
        const mono = clipQuietestMono(store, clip);
        if (!mono || !buf) { toast("No audio to sample."); return; }
        if (!hasAudioSignal(mono)) { toast("That clip's quiet part is silent — nothing to profile."); return; }
        setInsertProfile(fxId, buildNoiseProfile(mono, buf.sampleRate, "clip quiet part"));
        store.emitChange("mixer");
        toast("Noise profile captured for this insert.");
        renderPanel();
      }
      function clearProf() { setInsertProfile(fxId, null); store.emitChange("mixer"); toast("Profile cleared for this insert."); renderPanel(); }
      renderPanel();
      body.append(panel);
    }
    if (fx.kind === "deverb") {
      body.append(el("p", { class: "hint", text:
        "Removes the room's reverb tail live while keeping held notes at full level. Describe the room: Decay ≈ its RT60, Damping (low for tile/glass, high for soft rooms), Reverb Level (below 0 dB for a close mic, above for a distant mic or big room). Strength sets how hard the tail is cut. Adds ~21 ms latency." }));
    }
    // ---- presets: factory + user-saved (kind-scoped) ----
    const kind = fx.kind as Exclude<EffectKind, "wam">;
    const controls: { spec: Spec; range: HTMLInputElement; out: HTMLElement }[] = [];
    const syncControls = () => {
      for (const c of controls) {
        const v = Number(fx.params[c.spec.key] ?? EFFECT_DEFAULTS[kind][c.spec.key] ?? 0);
        c.range.value = String(v);
        c.out.textContent = `${v}${c.spec.unit ?? ""}`;
      }
    };
    const userPresets = () => (store.project.fxPresets ?? []).filter((p) => p.kind === kind);

    const presetSel = el("select", { class: "fx-preset-sel" }) as HTMLSelectElement;
    const delBtn = el("button", { class: "fx-preset-del", text: "🗑", attrs: { title: "Delete this saved preset" } }) as HTMLButtonElement;
    const rebuildPresetList = () => {
      presetSel.innerHTML = "";
      presetSel.append(el("option", { text: "Preset…", attrs: { value: "" } }));
      const facGrp = document.createElement("optgroup"); facGrp.label = "Factory";
      for (const p of FX_PRESETS[kind]) facGrp.append(el("option", { text: p.name, attrs: { value: "f:" + p.name } }));
      presetSel.append(facGrp);
      const mine = userPresets();
      if (mine.length) {
        const myGrp = document.createElement("optgroup"); myGrp.label = "Yours";
        for (const p of mine) myGrp.append(el("option", { text: p.name, attrs: { value: "u:" + p.id } }));
        presetSel.append(myGrp);
      }
      delBtn.style.display = "none";
    };
    const applyParams = (params: EffectParam) => {
      store.updateEffect(target, fxId, { params: { ...params } });
      syncControls();
    };
    presetSel.addEventListener("change", () => {
      const v = presetSel.value;
      delBtn.style.display = v.startsWith("u:") ? "" : "none";
      if (v.startsWith("f:")) applyParams(FX_PRESETS[kind].find((p) => p.name === v.slice(2))!.params);
      else if (v.startsWith("u:")) { const p = userPresets().find((p) => p.id === v.slice(2)); if (p) applyParams(p.params); }
    });
    delBtn.addEventListener("click", () => {
      const v = presetSel.value;
      if (!v.startsWith("u:")) return;
      store.deleteFxPreset(v.slice(2));
      rebuildPresetList();
    });
    const saveBtn = el("button", { class: "fx-preset-save", text: "Save preset…", attrs: { title: "Save the current settings as a reusable preset" } });
    saveBtn.addEventListener("click", () => {
      const input = el("input", { attrs: { type: "text", value: `My ${fx.name}` } }) as HTMLInputElement;
      const b = el("div", { class: "seq-save" }, [el("p", { class: "hint", text: `Save the current ${fx.name} settings as a preset.` }), input]);
      openModal("Save preset", b, [
        { label: "Cancel", onClick: () => {} },
        { label: "Save", primary: true, onClick: () => {
          const n = input.value.trim(); if (!n) { toast("Enter a name."); return false; }
          const preset: FxPreset = { id: uid("fxp"), kind, name: n, params: { ...fx.params } };
          store.saveFxPreset(preset);
          rebuildPresetList();
          presetSel.value = "u:" + preset.id; delBtn.style.display = "";
          toast(`Saved preset "${n}".`);
        } },
      ]);
      setTimeout(() => { input.focus(); input.select(); }, 0);
    });
    rebuildPresetList();
    body.append(el("div", { class: "fx-preset-row" }, [el("label", { text: "Preset" }), presetSel, delBtn, saveBtn]));

    for (const spec of SPECS[fx.kind]) {
      const row = el("div", { class: "fx-param" });
      const val = Number(fx.params[spec.key] ?? EFFECT_DEFAULTS[kind][spec.key] ?? 0);
      const range = el("input", { attrs: { type: "range", min: String(spec.min), max: String(spec.max), step: String(spec.step), value: String(val) } }) as HTMLInputElement;
      const out = el("output", { text: `${val}${spec.unit ?? ""}` });
      range.addEventListener("input", () => {
        const v = Number(range.value);
        out.textContent = `${v}${spec.unit ?? ""}`;
        store.updateEffect(target, fxId, { params: { [spec.key]: v } as any });
        presetSel.value = ""; delBtn.style.display = "none"; // tweaking = no longer a named preset
      });
      row.append(el("label", { text: spec.label }), range, out);
      controls.push({ spec, range, out });
      body.append(row);
    }
  }

  openModal(`${fx.name}`, body, [
    { label: "Remove", onClick: () => { store.removeEffect(target, fxId); } },
    { label: "Done", primary: true, onClick: () => {} },
  ]);
}
